import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Heart } from 'lucide-react';
import { KudosCard } from '../kudos';
import type { User } from '../../types';

interface KudosItem {
  id: string;
  fromUserId: string;
  fromUserName?: string;
  fromUserPhoto?: string;
  toUserId: string;
  toUserName?: string;
  message: string;
  createdAt: string;
}

interface KudosSectionProps {
  kudos: KudosItem[];
  loading: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
  users?: User[];
}

export const KudosSection: React.FC<KudosSectionProps> = ({
  kudos,
  loading,
  hasMore,
  onLoadMore,
  users = [],
}) => {
  const { t } = useTranslation('people');
  const { t: tCommon } = useTranslation('common');
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Heart className="h-5 w-5" />
          {t('kudos.title')}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {loading && kudos.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">{t('kudos.loading')}</div>
          ) : kudos.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              {t('kudos.noKudos')}
            </div>
          ) : (
            <>
              <div className="space-y-4">
                {kudos.map((kudo) => (
                  <KudosCard
                    key={kudo.id}
                    fromUserName={kudo.fromUserName || tCommon('unknown')}
                    fromUserPhoto={kudo.fromUserPhoto}
                    toUserName={kudo.toUserName || tCommon('unknown')}
                    message={kudo.message}
                    createdAt={kudo.createdAt}
                    users={users}
                  />
                ))}
              </div>

              {hasMore && (
                <div className="text-center pt-4">
                  <Button
                    variant="outline"
                    onClick={onLoadMore}
                    disabled={loading}
                  >
                    {loading ? tCommon('loading') : t('kudos.loadMore')}
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
